import { useMemo, useState } from "react";
import { supabase } from "@/lib/supabase";
import { useToast } from "@/components/feature/Toast";
import EmptyState from "@/pages/admin/dashboard/components/EmptyState";
import { formatPrice, productCover } from "@/pages/admin/dashboard/types";
import type { AdminProduct } from "@/pages/admin/dashboard/types";

interface FeaturedTabProps {
  products: AdminProduct[];
  onRefresh: () => void;
}

/**
 * Gestión de productos destacados. Los que se marquen aquí aparecen en el
 * carrusel "Productos destacados" de la portada.
 */
export default function FeaturedTab({ products, onRefresh }: FeaturedTabProps) {
  const { showToast } = useToast();
  const [busyId, setBusyId] = useState<number | null>(null);

  const activeProducts = useMemo(
    () => products.filter((p) => p.status === "active"),
    [products]
  );

  const featuredCount = activeProducts.filter((p) => p.featured).length;

  const toggleFeatured = async (product: AdminProduct) => {
    setBusyId(product.id);
    const { error } = await supabase
      .from("products")
      .update({ featured: !product.featured })
      .eq("id", product.id);
    setBusyId(null);
    if (error) {
      showToast("No se pudo actualizar el destacado.", "error");
      return;
    }
    showToast(
      product.featured
        ? `«${product.title}» dejó de ser destacado.`
        : `«${product.title}» ahora es destacado.`,
      "success"
    );
    onRefresh();
  };

  if (activeProducts.length === 0) {
    return (
      <EmptyState
        icon="ri-star-line"
        title="No hay publicaciones activas"
        description="Aprueba publicaciones en Moderación para poder destacarlas en la portada."
      />
    );
  }

  return (
    <div>
      <div className="flex items-center gap-2 border-b border-background-200/70 bg-background-100/60 px-5 py-3">
        <i className="ri-star-fill text-accent-500"></i>
        <p className="text-sm text-foreground-600">
          <span className="font-semibold text-foreground-900">
            {featuredCount}
          </span>{" "}
          {featuredCount === 1 ? "producto destacado" : "productos destacados"}{" "}
          en la portada
        </p>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full min-w-[720px] text-left">
          <thead>
            <tr className="border-b border-background-200/70 bg-background-100/60 text-xs font-semibold uppercase tracking-wide text-foreground-500">
              <th className="px-5 py-3">Publicación</th>
              <th className="px-5 py-3">Precio</th>
              <th className="px-5 py-3">Estado</th>
              <th className="px-5 py-3 text-right">Destacado</th>
            </tr>
          </thead>
          <tbody>
            {activeProducts.map((product) => {
              const cover = productCover(product);
              return (
                <tr
                  key={product.id}
                  className="border-b border-background-200/60 transition-colors last:border-0 hover:bg-background-100/40"
                >
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-3">
                      {cover ? (
                        <img
                          src={cover}
                          alt={product.title}
                          className="h-11 w-11 shrink-0 rounded-md border border-background-200 object-cover object-top"
                        />
                      ) : (
                        <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md bg-background-100">
                          <i className="ri-image-line text-lg text-foreground-400"></i>
                        </span>
                      )}
                      <div className="min-w-0">
                        <p className="max-w-[280px] truncate text-sm font-semibold text-foreground-950">
                          {product.title}
                        </p>
                        <p className="mt-0.5 text-xs text-foreground-500">
                          {product.category ?? "Sin categoría"}
                        </p>
                      </div>
                    </div>
                  </td>
                  <td className="px-5 py-3 text-sm font-semibold text-foreground-900">
                    {formatPrice(product.price, product.currency)}
                  </td>
                  <td className="px-5 py-3">
                    {product.featured ? (
                      <span className="inline-flex items-center gap-1 whitespace-nowrap rounded-full bg-accent-100 px-2.5 py-0.5 text-xs font-semibold text-accent-700">
                        <i className="ri-star-fill"></i>
                        En portada
                      </span>
                    ) : (
                      <span className="text-xs text-foreground-400">—</span>
                    )}
                  </td>
                  <td className="px-5 py-3">
                    <div className="flex justify-end">
                      <button
                        type="button"
                        onClick={() => toggleFeatured(product)}
                        disabled={busyId === product.id}
                        className={`inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md px-3 py-1.5 text-xs font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-60 ${
                          product.featured
                            ? "border border-background-300 bg-background-50 text-foreground-600 hover:bg-background-100"
                            : "bg-accent-500 text-white hover:bg-accent-600"
                        }`}
                      >
                        {busyId === product.id ? (
                          <i className="ri-loader-4-line animate-spin"></i>
                        ) : product.featured ? (
                          <i className="ri-star-off-line"></i>
                        ) : (
                          <i className="ri-star-line"></i>
                        )}
                        {product.featured ? "Quitar" : "Destacar"}
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}